import cuid from "cuid";
import { get, intersection, isEmpty, set, uniq } from "lodash";
import moment from "moment";
import omitDeep from "omit-deep-lodash";
import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import Header from "shared/components/Header";
import ProcessStepper from "shared/components/ProcessStepper";
import Loading from "shared/components/Spin";
import { useLazyQuery, useMutation } from "shared/hooks/useApi";
import graphql from "utils/api/graphql";
import { BUILD_GENERAL_COSTS } from "utils/api/graphql/mutations/costs";
import { GENERAL_COSTS } from "utils/api/graphql/queries/general-cost";
import { INITIAL_VALUES, INSURANCE_PLAN_PARAMS_RULES } from "utils/constants";
import { generateSQLCondition } from "utils/helpers/graphql";
import View from "./View";

const steps = [
  { title: "Qualification" },
  { title: "Sélection<br />produits" },
  { title: "Devis" },
  { title: "Souscription" },
  { title: "Documents" },
  { title: "Signature<br />En attente" },
  { title: "Souscription terminée" },
];

const SelectProducts = ({
  project,
  currentStatus,
  onNext,
  processId,
  onBack,
  setDisplayProject,
}) => {
  const { id } = useParams();
  const [nextLoading, setNextLoading] = useState(false);
  const [updateProject] = useMutation(graphql.mutations.UPDATE_PROJECT, {
    refetchQueries: [
      {
        query: graphql.queries.PROJECT,
        awaitRefetchQueries: true,
        variables: { where: { id: project?.id || id } },
      },
    ],
  });
  const [filter, setFilter] = useState({});
  const [upSellsFilter, setUpSellsFilter] = useState({});
  const take = 3;
  const upSellsTake = 4;
  const insurancePlanFilter = useMemo(() => {
    const issuanceDate = moment(get(project, "contract.issuanceDate")).startOf(
      "day",
    );
    const issuanceDateDifference = issuanceDate.diff(
      moment().startOf("day"),
      "days",
    );

    const [startKey, endKey] = project.ria
      ? [
          INSURANCE_PLAN_PARAMS_RULES.DELTA_START_RIA,
          INSURANCE_PLAN_PARAMS_RULES.DELTA_END_RIA,
        ]
      : [
          INSURANCE_PLAN_PARAMS_RULES.DELTA_START,
          INSURANCE_PLAN_PARAMS_RULES.DELTA_END,
        ];

    const sqlConditions = [
      generateSQLCondition(
        startKey,
        "r.value IS NULL OR CAST(r.value AS NUMERIC) <= :value",
        issuanceDateDifference,
      ),
      generateSQLCondition(
        endKey,
        `r.value IS NULL OR CAST(r.value AS NUMERIC) >= :value ${
          moment(get(project, "contract.issuanceDate")).year() ===
          moment().year()
            ? "OR CAST(r.value AS NUMERIC) = 0"
            : ""
        }`,
        issuanceDateDifference,
      ),
      generateSQLCondition(
        INSURANCE_PLAN_PARAMS_RULES.PROCESSES,
        `r.value IS NULL OR r.value @> to_jsonb(ARRAY[:value])::jsonb OR jsonb_array_length(r.value) = 0`,
        `'${processId}'`,
      ),
    ];

    return {
      rules: sqlConditions.filter(Boolean),
      custom: {
        AND: [
          {
            OR: [
              { startDate: null },
              {
                startDate: issuanceDate,
                operation: "<=",
              },
            ],
          },
          {
            OR: [
              { endDate: null },
              {
                endDate: issuanceDate,
                operation: ">=",
              },
            ],
          },
        ],
      },
    };
  }, []);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [selectedInsurancePlan, setSelectedInsurancePlan] = useState({
    insurancePlan: { id: get(project, "fields.selectedInsurancePlan") },
  });
  const [selectedCashback, setSelectedCashback] = useState({
    insurancePlan: { id: get(project, "fields.selectedCashback", "sans") },
  });
  const selectedInsurancePlanRef = useRef(selectedInsurancePlan);
  const selectedCashbackRef = useRef(selectedCashback);
  selectedInsurancePlanRef.current = selectedInsurancePlan;
  selectedCashbackRef.current = selectedCashback;
  const pendingSelectionCostsRef = useRef(Promise.resolve());
  const [selectedGeneralCosts] = useLazyQuery(GENERAL_COSTS, {
    onCompleted: ({ generalCosts: { data } }) => {
      const cleaned = omitDeep(data, "__typename");
      setSelectedProducts(cleaned);
      setSelectedInsurancePlan((prev) => {
        const prevId = get(prev, "insurancePlan.id");
        const next = cleaned.find(
          (gc) => get(gc, "insurancePlan.id") === prevId,
        );
        return next || prev;
      });
      setSelectedCashback((prev) => {
        if (get(prev, "insurancePlan.id") === "sans") return prev;
        const prevId = get(prev, "insurancePlan.id");
        const next = cleaned.find(
          (gc) => get(gc, "insurancePlan.id") === prevId,
        );
        return next || prev;
      });
    },
  });

  const [buildGeneralCosts] = useMutation(BUILD_GENERAL_COSTS, {
    variables: {
      data: {
        project: { id },
        insurancePlanFilter,
      },
    },
    onCompleted: ({ buildGeneralCosts }) => {
      const fromCompare =
        intersection(
          buildGeneralCosts?.map(({ insurancePlan }) => insurancePlan?.id),
          get(project, "fields.selectedToCompare", []),
        ) || [];

      const fromSelection = [
        get(selectedInsurancePlanRef.current, "insurancePlan.id"),
        get(selectedCashbackRef.current, "insurancePlan.id"),
      ].filter((planId) => planId && planId !== "sans");

      const idsToRefresh = uniq([...fromCompare, ...fromSelection]);

      pendingSelectionCostsRef.current = idsToRefresh.length
        ? (selectedGeneralCosts({
            variables: {
              where: { project: { id } },
              isIn: {
                insurancePlan: {
                  id: idsToRefresh,
                },
              },
            },
          }) ?? Promise.resolve())
        : Promise.resolve();

      setFilter({
        skip: 0,
        take,
        where: { project: { id }, insurancePlan: { isStandalone: true } },
        isIn: { id: buildGeneralCosts?.map(({ id }) => id) || [] },
      });
      setUpSellsFilter({
        skip: 0,
        take: upSellsTake,
        where: { project: { id }, insurancePlan: { isStandalone: false } },
        isIn: { id: buildGeneralCosts?.map(({ id }) => id) || [] },
      });
    },
  });
  useEffect(() => {
    if (project.locked) {
      selectedGeneralCosts({
        variables: {
          where: { project: { id } },
          isIn: {
            insurancePlan: { id: get(project, "fields.selectedToCompare", []) },
          },
        },
      });
      return setFilter({
        skip: INITIAL_VALUES.skip,
        take,
        where: { project: { id } },
      });
    }
    buildGeneralCosts();
  }, []);

  const handleRelatedPersonSave = (values, onSaved) => {
    const { members } = values;
    const relatedPersons = members.map((m) => ({
      id: m.id,
      relationship: m.relationship,
      identity: {
        id: cuid(),
        user: {
          id: cuid(),
          firstname: m.firstName,
          lastname: m.lastName,
          birthDate: m.birthDate || null,
        },
      },
    }));
    updateProject({
      variables: {
        where: { id: project.id },
        data: {
          contact: {
            ...get(project, "contact", {}),
            relatedPersons,
          },
        },
      },
      onCompleted: async ({ updateProject: updatedProjectData }) => {
        const updatedProject = omitDeep(updatedProjectData, "__typename");
        if (setDisplayProject) {
          set(updatedProject, "fields.isManualAssign", undefined);
          set(updatedProject, "author", undefined);
          setDisplayProject(updatedProject);
        }
        try {
          await buildGeneralCosts();
          await pendingSelectionCostsRef.current;
        } finally {
          onSaved?.();
        }
      },
    });
  };

  const onSubmit = () => {
    setNextLoading(true);
    onNext({
      payload: {
        fields: {
          ...project.fields,
          selectedToCompare: [get(selectedInsurancePlan, "insurancePlan.id")],
          insurancePlans: [get(selectedInsurancePlan, "insurancePlan.id")],
          upsells: {
            [get(selectedInsurancePlan, "insurancePlan.id")]:
              !get(selectedCashback, "cost") ||
              Number.isNaN(get(selectedCashback, "cost"))
                ? []
                : [
                    {
                      id: get(selectedCashback, "id"),
                      cost: get(selectedCashback, "cost"),
                      costDetails: get(selectedCashback, "costDetails"),
                      currency: get(selectedCashback, "currency"),
                      commission: get(selectedCashback, "commission"),
                      insurancePlan: {
                        id: get(selectedCashback, "insurancePlan.id"),
                        name: get(selectedCashback, "insurancePlan.name"),
                        alias: get(selectedCashback, "insurancePlan.alias"),
                        category: get(
                          selectedCashback,
                          "insurancePlan.category",
                        ),
                        description: get(
                          selectedCashback,
                          "insurancePlan.description",
                        ),
                        supportingInformation: get(
                          selectedCashback,
                          "insurancePlan.supportingInformation",
                        ),
                      },
                    },
                  ],
          },
        },
      },
      onCompleted: () => setNextLoading(false),
    });
  };

  if (isEmpty(filter)) return <Loading />;

  return (
    <>
      <Header key="header" title="Sélection des Produits" actions={[]} />
      <ProcessStepper
        steps={steps}
        current={+get(currentStatus, "order")}
        aria-label="Étapes du projet"
      />
      <View
        nextLoading={nextLoading}
        onSubmit={onSubmit}
        project={project}
        onBack={onBack}
        selectedInsurancePlan={selectedInsurancePlan}
        setSelectedInsurancePlan={setSelectedInsurancePlan}
        selectedCashback={selectedCashback}
        setSelectedCashback={setSelectedCashback}
        filter={filter}
        upSellsFilter={upSellsFilter}
        take={take}
        loading={isEmpty(filter)}
        handleRelatedPersonSave={handleRelatedPersonSave}
      />
    </>
  );
};

export default SelectProducts;
